<script setup>
import axios from 'axios'
import { Message } from '@arco-design/web-vue'
import QRCode from 'qrcode'
import { useI18n } from 'vue-i18n'
import EasyLightbox from 'vue-easy-lightbox'
import RecentlyUpdated from '@/views/new/components/RecentlyUpdated.vue'
import { parseHeziyunData } from '@/utils/heziyunData'

const { t } = useI18n()

const HeziyunData = ref(parseHeziyunData().global || {})

const visible = ref(false)
const index = ref(0)
const imgs = ref([])

const detailsContent = ref({})
const prev = ref({ id: '/' })
const next = ref({ id: '/' })
const isMobile = ref(false)
const showQRCode = ref(false)
const qrCodeUrl = ref('')
const address = ref(null)

const addressType = computed(() => {
  const map = {
    news: 1,
    announcement: 2
  }
  return map[address.value] || null
})

const decodeHTML = (html = '') => {
  if (!html) return ''
  const txt = document.createElement('textarea')
  txt.innerHTML = html
  txt.innerHTML = txt.value
  return txt.value
}

const resetSkeleton = () => {}

const popupNotification = (title, text, type = 'error') => {
  const fn = type === 'success' ? Message.success : Message.error
  fn({
    content: `${title} - ${text}`,
    position: 'top'
  })
}

const getId = () => {
  const { pathname } = window.location
  const segments = pathname.split('/').filter(Boolean)

  const addressVal = segments[0] || null
  const idText = segments[1] || ''
  const id = idText.split('.')[0]

  address.value = addressVal
  getNewsDetails(addressVal, /^\d+$/.test(id) ? id : null)
}

const getNewsDetails = async (addressParam, newsId) => {
  try {
    resetSkeleton()

    let apiUrl = addressParam === 'help'
      ? `/console/v1/${addressParam}/${newsId}?id=${newsId}`
      : `/console/v1/${addressParam}/${newsId}`

    // 保留你原来的固定测试逻辑，不改原逻辑
    // apiUrl = `/console/v1/announcement/13`
    // addressParam = 'announcement'

    const { data } = await axios.get(apiUrl)
    const res = data?.data || {}

    const buildObject = {
      news: res.news || {},
      announcement: res.announcement || {},
      help: {
        ...(res.help || {}),
        type: '帮助中心'
      }
    }

    const current = buildObject[addressParam] || {}

    detailsContent.value = current
    prev.value = current.prev || { id: '/' }
    next.value = current.next || { id: '/' }
  } catch (error) {
    console.error('获取详情失败:', error)
    popupNotification('加载失败', '无法获取内容详情，请稍后再试', 'error')
  }
}

const formatTimestamp = (timestamp) => {
  if (!timestamp) return ''

  const date = new Date(Number(timestamp) * 1000)
  const pad = n => String(n).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const detectDeviceType = () => {
  const ua = navigator.userAgent.toLowerCase()
  isMobile.value =
    /mobile|android|iphone|ipad|phone|micromessenger|qq\//i.test(ua) ||
    window.innerWidth <= 768
}

const copyLink = async (name) => {
  const currentUrl = window.location.href

  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(currentUrl)
    } else {
      const textarea = document.createElement('textarea')
      textarea.value = currentUrl
      textarea.style.cssText = 'position:fixed;left:-9999px;top:-9999px;opacity:0;'
      document.body.appendChild(textarea)
      textarea.focus()
      textarea.select()

      const ok = document.execCommand('copy')
      document.body.removeChild(textarea)

      if (!ok) throw new Error('复制失败')
    }

    popupNotification(
      '链接已复制到剪贴板',
      `${name}提醒您 复制成功！请切换到目标窗口，使用 Ctrl+V 或 Cmd+V 粘贴。`,
      'success'
    )
  } catch (err) {
    console.error('复制失败:', err)
    popupNotification(
      '复制失败，请手动复制链接',
      `${name}提醒您 无法访问剪贴板！请检查浏览器或系统权限。`,
      'error'
    )
  }
}

const generateQRCode = async () => {
  try {
    qrCodeUrl.value = await QRCode.toDataURL(window.location.href, {
      width: 300,
      margin: 2,
      color: {
        dark: '#000000',
        light: '#FFFFFF'
      },
      errorCorrectionLevel: 'H'
    })

    showQRCode.value = true
  } catch (err) {
    console.error('生成二维码失败:', err)
    Message.error({
      content: '生成二维码失败，请稍后重试',
      position: 'top'
    })
  }
}

const shareToWeChat = () => {
  const ua = navigator.userAgent.toLowerCase()
  const inWeChat = /micromessenger/i.test(ua)

  if (isMobile.value && inWeChat && window.WeixinJSBridge) {
    try {
      window.WeixinJSBridge.invoke('shareTimeline', {
        title: document.title,
        link: window.location.href,
        imgUrl: 'https://via.placeholder.com/100x100.png?text=LOGO',
        desc: detailsContent.value.title || '分享内容'
      })
      return
    } catch (error) {
      console.warn('微信分享失败，使用二维码方式:', error)
    }
  }

  generateQRCode()
}

const shareToQQ = () => {
  const inQQ = /qq\//i.test(navigator.userAgent.toLowerCase())

  if (inQQ && typeof window.mqq !== 'undefined') {
    try {
      window.mqq.ui.shareMessage({
        share_type: 'web',
        title: document.title,
        desc: detailsContent.value.title || '分享内容',
        share_url: window.location.href,
        image_url: 'https://via.placeholder.com/100x100.png?text=LOGO',
        back: true
      })
      return
    } catch (error) {
      console.warn('QQ分享失败:', error)
      popupNotification('分享失败', 'QQ分享功能调用失败，请使用其他方式分享', 'error')
      return
    }
  }

  const url = `https://connect.qq.com/widget/shareqq/index.html?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(document.title)}&desc=${encodeURIComponent(detailsContent.value.title || '')}&summary=${encodeURIComponent(detailsContent.value.title || '')}&site=${encodeURIComponent(HeziyunData.value.webname || '网站')}`

  window.open(url, '_blank', 'width=615,height=505')
}

const closeQRCode = () => {
  showQRCode.value = false
  qrCodeUrl.value = ''
}

const parseImagesFromContent = () => {
  const content = detailsContent.value.content

  if (!content) {
    imgs.value = []
    return
  }

  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = decodeHTML(content)

  imgs.value = Array.from(tempDiv.querySelectorAll('img')).map(img => ({
    src: img.getAttribute('src') || img.src,
    title: img.alt || detailsContent.value.title || ''
  }))
}

const showLightbox = (idx) => {
  index.value = idx
  visible.value = true
}

const onImgClick = (e) => {
  const target = e.target

  if (!target || target.tagName !== 'IMG') return

  const contentElement = e.currentTarget
  const allImgs = Array.from(contentElement.querySelectorAll('img'))
  const currentIndex = allImgs.indexOf(target)

  if (currentIndex !== -1) {
    showLightbox(currentIndex)
  }
}

const addImageClickListeners = () => {
  const contentElement = document.querySelector('.Heziyun-content')
  if (!contentElement) return

  contentElement.removeEventListener('click', onImgClick)
  contentElement.addEventListener('click', onImgClick)
}

const handleKeyDown = (e) => {
  if (e.key === 'Escape' && showQRCode.value) {
    closeQRCode()
  }
}

watch(
  () => detailsContent.value.content,
  async (content) => {
    if (!content) return

    await nextTick()
    parseImagesFromContent()
    addImageClickListeners()
  },
  { immediate: true }
)

onMounted(() => {
  getId()
  detectDeviceType()

  window.addEventListener('resize', detectDeviceType, { passive: true })
  window.addEventListener('keydown', handleKeyDown)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', detectDeviceType)
  window.removeEventListener('keydown', handleKeyDown)

  const contentElement = document.querySelector('.Heziyun-content')
  if (contentElement) {
    contentElement.removeEventListener('click', onImgClick)
  }
})
</script>

<template>
  <!-- 在模板末尾添加EasyLightbox组件 -->
  <EasyLightbox :visible="visible" :imgs="imgs" :index="index" @hide="visible = false" />

  <!-- 标签头 -->
  <div class="Heziyun-headLabel">
    <div class="Heziyun-defaultWidth">
      <a class="Heziyun-a" href="/">{{ $t('newsDetail.home') }}</a>
      <i>/</i>
      <a class="Heziyun-a" :href="`/new_Smell.html?Class=${addressType}`">{{ $t('newsDetail.announcementHome')
      }}</a>
      <i>/</i>
      <p class="Heziyun-a" v-skeleton="{ show: !detailsContent.title }">
        {{ detailsContent.title }}
      </p>
    </div>
  </div>

  <!-- 新闻详情 -->
  <div class="Heziyun-detailsContent">
    <div class="Heziyun-defaultWidth">
      <div class="Heziyun-flexupperPart">
        <!-- 主要内容 -->
        <div class="Heziyun-primaryColor">
          <!-- 标题 -->
          <div class="Heziyun-title" v-skeleton="{ show: !detailsContent.title }">
            {{ detailsContent.title }}
          </div>

          <!-- 关键词 -->
          <div class="Heziyun-keyWord" v-skeleton="{ show: !detailsContent.keywords }" v-show="!detailsContent.title">
            {{ detailsContent.keywords ? t('newsDetail.keywords') + detailsContent.keywords :
              t('newsDetail.noKeywords') }}
          </div>

          <!-- 来源：新闻公告 发布时间 -->
          <div class="Heziyun-double">
            <div class="Heziyun-source" v-skeleton="{ show: !detailsContent.type }">
              {{ t('newsDetail.source') }}<span>{{ detailsContent.type }}</span>
            </div>
            <div class="Heziyun-time" v-skeleton="{ show: !detailsContent.create_time }">
              {{ detailsContent.create_time ? t('newsDetail.publishTime') +
                formatTimestamp(detailsContent.create_time) : '' }}
            </div>
          </div>

          <!-- 富文本框填写详细内容 -->
          <div class="Heziyun-content" v-html="decodeHTML(detailsContent.content)"
            v-skeleton="{ show: !detailsContent.content }"></div>

          <!-- 分享链接 -->
          <div class="Heziyun-shareLink">
            <div class="Heziyun-item" @click="copyLink(HeziyunData.webname)"
              v-skeleton="{ show: !detailsContent.title }">
              <img src="@/static/img/new_Smell/link.svg" :alt="t('newsDetail.shareLinkAlt')" />
              {{ $t('newsDetail.shareLink') }}
            </div>
            <div class="Heziyun-item" @click="shareToWeChat" v-skeleton="{ show: !detailsContent.title }">
              <img src="@/static/img/new_Smell/wx.svg" :alt="t('newsDetail.shareWeChatAlt')" />
              {{ $t('newsDetail.shareWeChat') }}
            </div>
            <div class="Heziyun-item" @click="shareToQQ" v-skeleton="{ show: !detailsContent.title }">
              <img src="@/static/img/new_Smell/qq.svg" :alt="t('newsDetail.shareQQAlt')" />
              {{ $t('newsDetail.shareQQ') }}
            </div>
          </div>
        </div>

        <!-- 上一页下一页 -->
        <div class="Heziyun-upAndDownPages">
          <a class="Heziyun-left" :href="`/${address}/${next.id}.html`" v-show="next.title"
            v-skeleton="{ show: !next.title }">
            <span>{{ $t('newsDetail.previous') }}</span>
            《
            <p>{{ next ? next.title : '' }}</p>
            》
          </a>
          <a class="Heziyun-right" :href="`/${address}/${prev.id}.html`" v-show="prev.title"
            v-skeleton="{ show: !prev.title }">
            <span>{{ $t('newsDetail.next') }}</span>
            《
            <p>{{ prev ? prev.title : '' }}</p>
            》
          </a>
        </div>
      </div>

      <!-- 置顶 -->
      <RecentlyUpdated :detailscontent="t('newsDetail.newsAnnouncement')"></RecentlyUpdated>
    </div>
  </div>

  <!-- 二维码弹窗 -->
  <a-modal v-model:visible="showQRCode" title="微信分享" :footer="false" :mask-closable="true" :closable="true"
    @cancel="closeQRCode" width="360px" :body-style="{ padding: '0' }">
    <div class="qrcode-modal">
      <div class="qrcode-header">
        <h3>微信扫描二维码分享</h3>
      </div>
      <div class="qrcode-content">
        <div class="qrcode-image">
          <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="分享二维码" @error="generateQRCode" />
          <div v-else class="qrcode-loading">
            <a-spin :size="32" />
            <p>正在生成二维码...</p>
          </div>
        </div>
        <div class="qrcode-tips">
          <div class="tip-item">
            <div class="tip-icon">1</div>
            <span>打开微信，点击右上角"+"</span>
          </div>
          <div class="tip-item">
            <div class="tip-icon">2</div>
            <span>选择"扫一扫"</span>
          </div>
          <div class="tip-item">
            <div class="tip-icon">3</div>
            <span>扫描上方二维码</span>
          </div>
          <div class="tip-item">
            <div class="tip-icon">4</div>
            <span>点击右上角"..."分享</span>
          </div>
        </div>
        <div class="qrcode-actions">
          <a-button type="primary" @click="copyLink(HeziyunData.webname)" block>
            <template #icon>
              <icon-copy />
            </template>
            复制链接
          </a-button>
        </div>
      </div>
    </div>
  </a-modal>
</template>

<style>
@import "@/static/css/import";
@import "@/static/font/font.css";

body {
  background-color: #f5f7fc;
}

@media (max-width: 768px) {
  :deep(.arco-modal) {
    width: 80% !important;
  }
}

.Heziyun-content img {

  object-fit: cover;
  display: block;
  border-radius: 0.5rem;
  -webkit-border-radius: 0.5rem;
  -moz-border-radius: 0.5rem;
  -ms-border-radius: 0.5rem;
  -o-border-radius: 0.5rem;
  max-width: 100%;
  height: auto;
  margin: 0 auto;
}

.vel-img {
  box-shadow: none !important;
}
</style>

<style lang="less" scoped>
@font-face {
  font-family: "Title";
  font-weight: 400;
  src: url("@/static/font/file/AlimamaShuHeiTi-Bold.woff2") format("woff2");
  font-display: swap;
}

// 标签头
.Heziyun-headLabel {
  padding-top: 5rem;
  font-family: "Title";

  .Heziyun-defaultWidth {
    padding: 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  i {
    font-style: inherit;
    color: #6b7280;
  }

  a {
    color: #6b7280;
    font-size: 0.85rem;

    &:hover {
      color: #06f;
    }
  }

  p {
    color: #111827;
    font-size: 0.85rem;
  }
}

// 二维码弹窗样式
.qrcode-modal {
  .qrcode-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    justify-content: center;
    padding: 20px 20px 0 20px;

    h3 {
      margin: 0;
      font-size: 18px;
      font-weight: 600;
      color: #1d2129;
    }

    .close-btn {
      color: #86909c;

      &:hover {
        color: #165dff;
      }
    }
  }

  .qrcode-content {
    padding: 20px;

    .qrcode-image {
      width: 250px;
      height: 250px;
      margin: 0 auto 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid #f2f3f5;
      border-radius: 8px;
      overflow: hidden;
      background: #fff;

      img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        display: block;
      }

      .qrcode-loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: #86909c;

        p {
          margin-top: 10px;
          font-size: 14px;
        }
      }
    }

    .qrcode-tips {
      margin: 20px 0 30px;

      .tip-item {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        &:last-child {
          margin-bottom: 0;
        }

        .tip-icon {
          width: 24px;
          height: 24px;
          background: #165dff;
          color: #fff;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 500;
          margin-right: 12px;
          flex-shrink: 0;
        }

        span {
          font-size: 14px;
          color: #4e5969;
          line-height: 1.5;
        }
      }
    }

    .qrcode-actions {
      button {
        height: 40px;
        font-size: 14px;
        font-weight: 500;

        :deep(.arco-icon) {
          font-size: 16px;
        }
      }
    }
  }
}

// 组件库样式修订
@media (max-width: 768px) {
  :deep(.arco-modal) {
    width: 80% !important;
  }

  // 二维码弹窗移动端适配
  .qrcode-modal {
    .qrcode-header {
      padding: 15px 15px 0 15px;

      h3 {
        font-size: 16px;
      }
    }

    .qrcode-content {
      padding: 15px;

      .qrcode-image {
        width: 200px;
        height: 200px;
        margin-bottom: 15px;
      }

      .qrcode-tips {
        margin: 15px 0 20px;

        .tip-item {
          margin-bottom: 10px;

          .tip-icon {
            width: 20px;
            height: 20px;
            font-size: 11px;
            margin-right: 10px;
          }

          span {
            font-size: 13px;
          }
        }
      }

      .qrcode-actions {
        button {
          height: 36px;
          font-size: 13px;
        }
      }
    }
  }
}

// 主要详情内容
.Heziyun-detailsContent {
  .Heziyun-defaultWidth {
    display: flex;
    gap: 1.5rem;

    .Heziyun-flexupperPart {
      width: 75%;

      .Heziyun-primaryColor {
        background-color: #fff;
        border-radius: 1rem;
        -webkit-border-radius: 1rem;
        -moz-border-radius: 1rem;
        -ms-border-radius: 1rem;
        -o-border-radius: 1rem;
        padding: 1.5rem;
        display: flex;
        flex-direction: column;
        gap: 1rem;
        font-family: '思源黑';

        .Heziyun-title {
          color: rgb(76, 76, 76);
          font-size: 1.3rem;
          min-height: 1.8rem;
          font-family: "Title";
        }

        .Heziyun-keyWord {
          font-size: 0.8rem;
          color: rgb(132 136 151);
          min-height: 1.1rem;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 2;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: break-all;
        }

        .Heziyun-double {
          display: flex;
          align-items: center;
          gap: 1rem;
          font-size: 0.8rem;
          color: rgb(0 74 186);

          .Heziyun-time {
            font-size: 0.8rem;
            color: rgb(132 136 151);
            min-height: 1.2rem;
          }

          .Heziyun-source {
            span {
              background-color: rgb(2, 84, 255);
              padding: 0.1rem 0.5rem;
              color: #fff;
              border-radius: 50%;
              -webkit-border-radius: 110rem;
              -moz-border-radius: 50%;
              -ms-border-radius: 50%;
              -o-border-radius: 50%;
              font-size: 0.7rem;
            }
          }
        }

        .Heziyun-content {
          margin-top: 1rem;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          min-height: 20rem;
          line-height: 1.7;

          .Heziyun-slogan {
            background-color: #eff6ff;
            padding: 1rem;
            border-left: 0.2rem solid #3b82f6;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            color: #1e3a8a;
          }

          .Heziyun-subheading {
            color: #000;
            font-size: 1rem;
            min-height: 1.3rem;
          }

          .Heziyun-label {
            width: max-content;
            background-color: #fef2f2;
            color: #dc2626;
            padding: 0.4rem 1rem;
            border-radius: 0.3rem;
            -webkit-border-radius: 0.3rem;
            -moz-border-radius: 0.3rem;
            -ms-border-radius: 0.3rem;
            -o-border-radius: 0.3rem;
            min-height: 1.9rem;
          }

          img {
            object-fit: cover;
            display: block;
            border-radius: 0.5rem;
            -webkit-border-radius: 0.5rem;
            -moz-border-radius: 0.5rem;
            -ms-border-radius: 0.5rem;
            -o-border-radius: 0.5rem;
            max-width: 100%;
            height: auto;
            margin: 0 auto;
          }

          a {
            color: #06f;
            width: max-content;
            min-height: 1.3rem;
            display: flex;
            align-items: center;
            gap: 0.1rem;

            &:hover {
              color: #74a8f6;
              text-decoration: underline;
            }

            img {
              width: 1rem;
              height: 1rem
            }
          }

          .Heziyun-describe {
            padding: 1rem;
            border-radius: 1rem;
            -webkit-border-radius: 1rem;
            -moz-border-radius: 1rem;
            -ms-border-radius: 1rem;
            -o-border-radius: 1rem;
            background-color: #f9fafb;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            line-height: 1.3rem;

            span {
              color: #000;
              min-height: 1.3rem;
            }

            p:nth-child(2n + 1) {
              color: #000;
              min-height: 1.3rem;
            }

            p:nth-child(2n) {
              color: #4b5563;
              min-height: 1.3rem;
            }
          }
        }

        // 分享链接
        .Heziyun-shareLink {
          display: flex;
          align-items: center;
          gap: 1rem;

          .Heziyun-item {
            cursor: pointer;
            color: rgb(94, 94, 94);
            font-size: 0.8rem;
            display: flex;
            align-items: center;
            gap: 0.3rem;
            padding: 0.3rem 0.5rem;
            white-space: nowrap;
            transition: all 0.3s ease;

            &:hover {
              background-color: #f1f1f1;
            }

            &:active {
              transform: translateY(0);
            }

            img {
              width: 1.5rem;
              height: 1.5rem;
              object-fit: contain;
            }
          }
        }
      }
    }
  }
}

// 上一页下一页
.Heziyun-upAndDownPages {
  padding-top: 1rem;
  padding-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  a {
    &:hover {
      color: #3B82F6;
    }
  }

  .Heziyun-left {
    font-family: "思源黑";
    padding: 1rem;
    position: relative;
    display: flex;
    align-items: center;
    min-height: 3.2rem;
    margin-right: auto;
    background-color: #fff;
    border-radius: 0.5rem;
    -webkit-border-radius: 0.5rem;
    -moz-border-radius: 0.5rem;
    -ms-border-radius: 0.5rem;
    -o-border-radius: 0.5rem;
    width: calc(50% - 2rem);

    &:hover {
      background-color: rgb(2, 84, 255);
      color: #fff;

      p {
        color: #fff;
      }
    }

    span {
      color: rgb(189, 194, 216);
      white-space: nowrap;
    }

    p {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 75%;
    }
  }

  .Heziyun-right {
    font-family: "思源黑";
    padding: 1rem;
    position: relative;
    display: flex;
    align-items: center;
    min-height: 2rem;
    margin-left: auto;
    background-color: #fff;
    border-radius: 0.5rem;
    -webkit-border-radius: 0.5rem;
    -moz-border-radius: 0.5rem;
    -ms-border-radius: 0.5rem;
    -o-border-radius: 0.5rem;
    width: calc(50% - 2rem);

    &:hover {
      background-color: rgb(2, 84, 255);
      color: #fff;

      p {
        color: #fff;
      }
    }

    span {
      color: rgb(189, 194, 216);
      white-space: nowrap;
    }

    p {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 75%;
    }
  }
}

@media (max-width: 768px) {

  // 标签头
  .Heziyun-headLabel {
    padding-top: 15vw;

    .Heziyun-defaultWidth {
      padding: 5vw;
      gap: 1vw;
    }

    a {
      font-size: 3.8vw;
      flex-shrink: 0;
    }

    p {
      font-size: 3.8vw;
      max-width: 55vw;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  // 主要详情内容
  .Heziyun-detailsContent {
    .Heziyun-defaultWidth {
      padding: 0 5vw 5vw 5vw;
      gap: 3vw;
      flex-direction: column;

      .Heziyun-flexupperPart {
        width: 100%;

        .Heziyun-primaryColor {
          border-radius: 2vw;
          -webkit-border-radius: 2vw;
          -moz-border-radius: 2vw;
          -ms-border-radius: 2vw;
          -o-border-radius: 2vw;
          padding: 5vw;
          gap: 2vw;

          .Heziyun-title {
            font-size: 4.5vw;
            min-height: 5vw;
          }

          .Heziyun-keyWord {
            font-size: 3.5vw;
            min-height: 5vw;
          }

          .Heziyun-double {
            align-items: flex-start;
            flex-direction: column;
            gap: 3vw;
            font-size: 3.5vw;

            .Heziyun-source {
              span {
                padding: 0.3vw 3vw;
                font-size: 3vw;
              }
            }

            .Heziyun-time {
              font-size: 3.5vw;
              min-height: 5vw;
            }
          }

          .Heziyun-content {
            margin-top: 1vw;
            gap: 4vw;
            min-height: 60vw;
            font-size: 3.5vw;
            line-height: 1.7;

            .Heziyun-slogan {
              padding: 3vw;
              gap: 1vw;
              font-size: 3.8vw;
              border-left: 1vw solid #3b82f6;
            }

            .Heziyun-subheading {
              font-size: 4.5vw;
            }

            .Heziyun-label {
              padding: 1vw 3vw;
              border-radius: 2vw;
              -webkit-border-radius: 2vw;
              -moz-border-radius: 2vw;
              -ms-border-radius: 2vw;
              -o-border-radius: 2vw;
            }

            img {
              width: 100%;
              height: 100%;
              border-radius: 2vw;
              -webkit-border-radius: 2vw;
              -moz-border-radius: 2vw;
              -ms-border-radius: 2vw;
              -o-border-radius: 2vw;
            }

            a {
              gap: 1vw;

              img {
                width: 4vw;
                height: 4vw;
              }
            }

            .Heziyun-describe {
              padding: 3vw;
              border-radius: 2vw;
              -webkit-border-radius: 2vw;
              -moz-border-radius: 2vw;
              -ms-border-radius: 2vw;
              -o-border-radius: 2vw;
              gap: 2vw;
              line-height: 6vw;
              font-size: 3.8vw;
            }
          }

          // 分享链接
          .Heziyun-shareLink {
            gap: 1vw;
            flex-wrap: wrap;

            .Heziyun-item {
              font-size: 3.5vw;
              gap: 1vw;
              padding: 2vw 2vw;
              white-space: nowrap;
              flex: 1;
              justify-content: center;
              min-width: 0;

              img {
                width: 5vw;
                height: 5vw;
                flex-shrink: 0;
              }
            }
          }
        }
      }
    }
  }

  // 上一页下一页
  .Heziyun-upAndDownPages {
    padding-top: 3vw;
    padding-bottom: 0;
    gap: 3vw;
    flex-direction: column;

    .Heziyun-left {
      padding: 2vw 3vw;
      min-height: 10vw;
      border-radius: 1vw;
      -webkit-border-radius: 1vw;
      -moz-border-radius: 1vw;
      -ms-border-radius: 1vw;
      -o-border-radius: 1vw;
      width: 100%;

      span {
        font-size: 4vw;
      }

      p {
        font-size: 3.5vw;
      }
    }

    .Heziyun-right {
      padding: 2vw 3vw;
      min-height: 10vw;
      border-radius: 1vw;
      -webkit-border-radius: 1vw;
      -moz-border-radius: 1vw;
      -ms-border-radius: 1vw;
      -o-border-radius: 1vw;
      width: 100%;

      span {
        font-size: 4vw;
      }

      p {
        font-size: 3.5vw;
      }
    }
  }
}
</style>